import {
	ContentCopy,
	Download,
	HighlightOff,
	History,
	MoneyOff,
} from '@mui/icons-material';
import { Button, Card, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
	getCatchUpQueryKey,
	useCatchUp,
	useInvalidateInvoiceMutation,
	useWriteOffInvoiceMutation,
} from '../../../api/CatchUpsApi';
import {
	usePayInvoiceMutation,
	useReverseInvoiceMutation,
} from '../../../api/InvoiceApi';
import { getUsePolicyQueryKey } from '../../../api/PolicyApi';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { usePolicyContext } from '../../../context/policy';
import { CatchUpInvoice } from '../../../types/catchUps';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment } from '../../../types/payments';
import {
	PaymentReversalReason,
	PolicyInvoice2StatusAndTime,
} from '../../../types/policy';
import { formatCurrency } from '../../../util/util';
import DisplayCard from '../../DisplayCard';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import Error from '../../Error';
import InvoiceStateChip from '../../InvoiceStateChip';
import PayInvoiceModal from '../../payments/components/PayInvoiceModal';
import NewReversePaymentModal from '../../payments/components/ReversePaymentModal';
import WriteOffPaymentModal from '../../payments/components/WriteOffPaymentModal';
import { infoToast } from '../../Toast';
import InvalidateInvoiceModal from '../components/InvalidateInvoiceModal';
import PaymentsList from '../components/PaymentsList';
import {
	CATCHUP_PAGE__AMOUNT_DUE_TITLE,
	CATCHUP_PAGE__CATCHUP_NOT_FOUND,
	CATCHUP_PAGE__CATCHUP_NOT_FOUND_ACTION_TEXT,
	CATCHUP_PAGE__COPY_ACTION,
	CATCHUP_PAGE__COPY_ACTION__SUCCESS_TOAST,
	CATCHUP_PAGE__DOWNLOAD_ACTION,
	CATCHUP_PAGE__DUE_DATE_TITLE,
	CATCHUP_PAGE__INVALIDATE_ACTION,
	CATCHUP_PAGE__ISSUE_DATE_TITLE,
	CATCHUP_PAGE__PAY_ACTION,
	CATCHUP_PAGE__POLICYHOLDER_TITLE,
	CATCHUP_PAGE__POLICY_TITLE,
	CATCHUP_PAGE__REVERSE_ACTION,
	CATCHUP_PAGE__STATUS_TITLE,
	CATCHUP_PAGE__TITLE,
	CATCHUP_PAGE__WRITEOFF_ACTION,
} from '../data/constants';

interface InvoiceModalProps {
	open: boolean;
	catchUp?: CatchUpInvoice;
}

interface CatchUpInvoicePageProps {
	catchUp: CatchUpInvoice;
}

const CatchUpInvoicePage = (props: CatchUpInvoicePageProps) => {
	const { catchUp } = props;
	const authData = useAuthContext();
	const queryClient = useQueryClient();
	const policyData = usePolicyContext();
	const { policyholderId, policyId } = useParams();
	const { hasPermission, auth } = authData;
	const { authorizationToken: authToken } = auth;

	const [payInvoiceModalInfo, setPayInvoiceModalInfo] =
		React.useState<InvoiceModalProps>({ catchUp: null, open: false });
	const [writeOffInvoiceModal, setWriteOffInvoiceModal] =
		React.useState<InvoiceModalProps>({ open: false, catchUp: null });
	const [invalidateInvoiceModal, setInvalidateInvoiceModal] =
		React.useState<InvoiceModalProps>({ open: false, catchUp: null });
	const [reverseInvoiceModal, setReverseInvoiceModal] =
		React.useState<InvoiceModalProps>({ open: false, catchUp: null });

	const payInvoiceMutation = usePayInvoiceMutation(authToken, queryClient);
	const writeOffInvoiceMutation = useWriteOffInvoiceMutation(
		authToken,
		queryClient,
	);
	const invalidateInvoiceMutation = useInvalidateInvoiceMutation(
		authToken,
		queryClient,
	);
	const reverseInvoiceMutation = useReverseInvoiceMutation(
		authToken,
		queryClient,
	);

	const formattedAmount = formatCurrency(
		parseFloat(catchUp.totalDue),
		catchUp.totalDueCurrency as CurrencyCode,
	);
	const formattedCreatedDate = authData.getZonedFormattedDateString(
		catchUp.createdTimestamp,
	);
	const formattedDueDate = authData.getZonedFormattedDateString(
		catchUp.dueTimestamp,
	);
	const settlementStatus =
		catchUp.settlementStatus === 'settled'
			? catchUp.settlementType
			: catchUp.settlementStatus;

	const writtenOff = catchUp.statuses.some(
		(status: PolicyInvoice2StatusAndTime) => status.status === 'writtenOff',
	);
	const actions: HiddenActionItem[] = [];

	// outstanding invoice actions at policy level
	if (settlementStatus === 'outstanding') {
		if (hasPermission('invoice.writeOff') && !writtenOff) {
			actions.push({
				label: CATCHUP_PAGE__WRITEOFF_ACTION,
				icon: <MoneyOff />,
				callback: () => {
					setWriteOffInvoiceModal({ open: true, catchUp });
				},
			});
		}

		actions.push({
			label: CATCHUP_PAGE__INVALIDATE_ACTION,
			icon: <HighlightOff />,
			callback: () => {
				setInvalidateInvoiceModal({ open: true, catchUp });
			},
		});
	}

	// paid invoice actions
	if (settlementStatus === 'paid') {
		actions.push({
			label: CATCHUP_PAGE__REVERSE_ACTION,
			icon: <History />,
			callback: () => {
				setReverseInvoiceModal({ open: true, catchUp });
			},
		});
	}

	// default actions
	if (catchUp.documents[0]?.url) {
		actions.push({
			label: CATCHUP_PAGE__DOWNLOAD_ACTION,
			icon: <Download />,
			callback: () => {
				window.open(catchUp.documents[0].url, '_blank');
			},
		});
	}
	actions.push({
		label: CATCHUP_PAGE__COPY_ACTION,
		icon: <ContentCopy />,
		callback: () => {
			navigator.clipboard.writeText(catchUp.locator);
			infoToast(CATCHUP_PAGE__COPY_ACTION__SUCCESS_TOAST);
		},
	});

	const refetchData = (policyId?: string) => {
		const policyQueryKey = getUsePolicyQueryKey(policyId, authToken);
		const invoiceQueryKey = getCatchUpQueryKey(catchUp.locator, authToken);

		queryClient.invalidateQueries(invoiceQueryKey);
		queryClient.invalidateQueries(policyQueryKey);
	};

	const payInvoice = async (
		policyId: string,
		locator: string,
		paymentCreateReq: any,
	): Promise<Payment> => {
		const response = await payInvoiceMutation.mutateAsync({
			paymentCreateReq,
			invoiceLocator: locator,
			isCatchUpInvoice: true,
		});
		refetchData(policyId);
		return response.data;
	};

	const writeOffInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await writeOffInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyId);
	};

	const invalidateInvoice = async (
		invoiceLocator: string,
		policyLocator: string,
	) => {
		await invalidateInvoiceMutation.mutateAsync({ invoiceLocator });
		refetchData(policyId);
	};

	const reversePayment = async (
		paymentLocator: string,
		policyLocator: string,
		reversalReason: PaymentReversalReason,
	) => {
		await reverseInvoiceMutation.mutateAsync({
			paymentLocator,
			reversalReason,
		});
		refetchData(policyId);
	};

	return (
		<>
			<Stack spacing={2} alignItems='flex-start'>
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='center'
					sx={{ width: '100%' }}
				>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Typography variant='h3'>
							{CATCHUP_PAGE__TITLE(catchUp.displayId)}
						</Typography>
						{catchUp.settlementStatus === 'outstanding' &&
							hasPermission('invoice.pay') && (
								<Button
									variant='contained'
									onClick={() =>
										setPayInvoiceModalInfo({ open: true, catchUp })
									}
								>
									{CATCHUP_PAGE__PAY_ACTION}
								</Button>
							)}
					</Stack>
					<DropdownButton actions={actions} />
				</Stack>
				<Stack direction='row' spacing={2} alignItems='center'>
					<DisplayCard
						title={CATCHUP_PAGE__AMOUNT_DUE_TITLE}
						value={formattedAmount}
					/>
					<DisplayCard
						title={CATCHUP_PAGE__POLICYHOLDER_TITLE}
						value={policyholderId || policyData?.policy?.policyholderLocator}
					/>
					<DisplayCard
						title={CATCHUP_PAGE__POLICY_TITLE}
						value={
							<Link
								component={RouterLink}
								to={`/policy/${catchUp.policyLocator}`}
							>
								{catchUp.policyLocator || '-'}
							</Link>
						}
					/>
					<DisplayCard
						title={CATCHUP_PAGE__ISSUE_DATE_TITLE}
						value={formattedCreatedDate}
					/>
					<DisplayCard
						title={CATCHUP_PAGE__DUE_DATE_TITLE}
						value={formattedDueDate}
					/>
					<DisplayCard
						title={CATCHUP_PAGE__STATUS_TITLE}
						value={<InvoiceStateChip state={settlementStatus} />}
					/>
				</Stack>
			</Stack>
			<PaymentsList catchUp={catchUp} />
			{payInvoiceModalInfo?.catchUp && (
				<PayInvoiceModal
					callback={payInvoice}
					isCallbackLoading={false}
					isCallbackSuccess={false}
					open={payInvoiceModalInfo.open}
					setOpen={() => {
						setPayInvoiceModalInfo({
							open: false,
						});
					}}
					invoice={payInvoiceModalInfo.catchUp}
					isCatchUp
				/>
			)}
			{writeOffInvoiceModal?.catchUp && (
				<WriteOffPaymentModal
					callback={writeOffInvoice}
					open={writeOffInvoiceModal.open}
					handleClose={() => {
						setWriteOffInvoiceModal({
							open: false,
							catchUp: null,
						});
					}}
					invoice={writeOffInvoiceModal.catchUp}
					isCatchUp
				/>
			)}
			{invalidateInvoiceModal?.catchUp && (
				<InvalidateInvoiceModal
					callback={invalidateInvoice}
					open={invalidateInvoiceModal.open}
					handleClose={() => {
						setInvalidateInvoiceModal({
							open: false,
							catchUp: null,
						});
					}}
					invoice={invalidateInvoiceModal.catchUp}
					isCatchUp
				/>
			)}
			{reverseInvoiceModal?.catchUp && (
				<NewReversePaymentModal
					callback={reversePayment}
					open={reverseInvoiceModal.open}
					handleClose={() => {
						setReverseInvoiceModal({
							open: false,
							catchUp: null,
						});
					}}
					invoice={reverseInvoiceModal.catchUp}
					isCatchUp
				/>
			)}
		</>
	);
};

const CatchUpInvoicePageLoadingSkeleton = () => {
	return (
		<Stack spacing={2} alignItems='flex-start'>
			<Stack direction='row' spacing={2} alignItems='center'>
				<Typography variant='h3'>
					<Skeleton variant='text' width={80} />
				</Typography>
			</Stack>
			<Stack direction='row' spacing={2} alignItems='center'>
				<DisplayCard title={CATCHUP_PAGE__AMOUNT_DUE_TITLE} loading />
				<DisplayCard title={CATCHUP_PAGE__ISSUE_DATE_TITLE} loading />
				<DisplayCard title={CATCHUP_PAGE__DUE_DATE_TITLE} loading />
				<DisplayCard title={CATCHUP_PAGE__STATUS_TITLE} loading />
				<DisplayCard title={CATCHUP_PAGE__POLICY_TITLE} loading />
			</Stack>
			<Card sx={{ width: '100%', height: 96 }}></Card>
		</Stack>
	);
};

const CatchUpInvoiceWrapper = () => {
	const { catchUpLocator } = useParams();
	const navigate = useNavigate();
	const authData = useAuthContext();
	const authToken = authData.auth?.authorizationToken;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const {
		data: catchUpInvoice,
		isLoading,
		isError,
		error,
	} = useCatchUp(
		catchUpLocator,
		authToken,
		cashManagementEnabled,
		authData.hasPermission('catchUpInvoice.fetch'),
	);

	if (isError && axios.isAxiosError(error)) {
		return (
			<Error
				error={
					error?.response?.data.message ||
					`${CATCHUP_PAGE__CATCHUP_NOT_FOUND} ${catchUpLocator}`
				}
				primaryAction={() => navigate('/catchUps')}
				primaryActionText={CATCHUP_PAGE__CATCHUP_NOT_FOUND_ACTION_TEXT}
			/>
		);
	}

	if (isLoading) {
		return <CatchUpInvoicePageLoadingSkeleton />;
	}

	return <CatchUpInvoicePage catchUp={catchUpInvoice} />;
};

export default CatchUpInvoiceWrapper;
